import type { TFunction } from 'i18next';
import type { SyncBatch } from 'api/new-analytics/types';
import type { PageData, ProfileGroup } from 'types/Page';

export interface AnalyticsSyncRunLabelParams {
  isGroup: boolean;
  groupId: number | null;
  pageId: number | null;
  pageIdsNormalized: readonly number[];
  pages: readonly PageData[];
  profileGroups: readonly ProfileGroup[];
  syncBatches: readonly SyncBatch[];
  t: TFunction;
}

function uniqueSortedPageIdsFromBatches(
  syncBatches: readonly SyncBatch[],
): number[] {
  return Array.from(
    new Set(
      syncBatches
        .map((batch) => batch.page_id)
        .filter((id) => Number.isFinite(id)),
    ),
  ).sort((leftPageId, rightPageId) => leftPageId - rightPageId);
}

export function formatAnalyticsSyncRunLabel({
  isGroup,
  groupId,
  pageId,
  pageIdsNormalized,
  pages,
  profileGroups,
  syncBatches,
  t,
}: AnalyticsSyncRunLabelParams): string {
  if (isGroup && groupId != null) {
    const name = profileGroups
      .find((group) => group.id === groupId)
      ?.name?.trim();
    if (name) {
      return name;
    }
  }

  if (!isGroup && pageId != null) {
    const name = pages.find((page) => page.id === pageId)?.name?.trim();
    if (name) {
      return name;
    }
    return t('analyticsSyncUnknownProfile', { id: pageId });
  }

  const idSource =
    pageIdsNormalized.length > 0
      ? [...pageIdsNormalized]
      : uniqueSortedPageIdsFromBatches(syncBatches);

  if (idSource.length === 0) {
    return t('analyticsSyncRunLabelFallback');
  }

  if (idSource.length === 1) {
    const onlyId = idSource[0];
    const name = pages.find((page) => page.id === onlyId)?.name?.trim();
    return name ?? t('analyticsSyncUnknownProfile', { id: onlyId });
  }

  const firstId = idSource[0];
  const firstName =
    pages.find((page) => page.id === firstId)?.name?.trim() ??
    t('analyticsSyncUnknownProfile', { id: firstId });
  const rest = idSource.length - 1;
  return t('analyticsSyncRunLabelManyProfiles', {
    name: firstName,
    count: rest,
  });
}
